import React, { useCallback, useEffect } from "react";
import { X, ArrowLeft, ArrowRight } from "lucide-react";

/**
 * Props:
 *  - list: array of film objects (each may have title, type, year, director, directorLabel, agency, youtubeId)
 *  - index: current index into list (null when closed)
 *  - open: boolean
 *  - onIndexChange: (newIndex) => void
 *  - onClose: () => void
 *  - meta (legacy): if provided, used instead of list/index (for Home carousel single-play case)
 *  - videoId (legacy): used with meta
 */
const VideoModal = ({
  list,
  index,
  onIndexChange,
  open,
  onClose,
  meta,
  videoId: legacyVideoId,
}) => {
  const usingList = Array.isArray(list) && typeof index === "number";
  const current = usingList ? list[index] : null;
  const videoId = usingList ? current?.youtubeId : legacyVideoId;

  const title = usingList ? current?.title : meta?.title;
  const typeLine = (() => {
    const t = usingList ? current?.type : meta?.type;
    const y = usingList ? current?.year : meta?.year;
    if (!t) return null;
    return y ? `${t} ${y}` : t;
  })();
  const director = usingList ? current?.director : meta?.director;
  const directorLabel = (usingList ? current?.directorLabel : meta?.directorLabel) || "Dir";
  const agency = usingList ? current?.agency : meta?.agency;
  const description = usingList ? current?.description : meta?.description;

  const go = useCallback(
    (dir) => {
      if (!usingList || !list.length) return;
      const total = list.length;
      let next = (index + dir + total) % total;
      // skip films with no youtubeId when navigating
      let guard = 0;
      while (!list[next]?.youtubeId && guard < total) {
        next = (next + dir + total) % total;
        guard += 1;
      }
      onIndexChange(next);
    },
    [usingList, list, index, onIndexChange],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    if (open) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, go]);

  if (!open || !videoId) return null;

  const showFilmArrows = usingList && list.length > 1;

  return (
    <div
      className="fixed inset-0 z-[100] bg-canvas/95 backdrop-blur-sm flex items-center justify-center px-4 md:px-10 py-10"
      onClick={onClose}
      data-testid="video-modal"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        data-testid="video-modal-close"
        className="absolute top-6 right-6 w-11 h-11 rounded-full border border-ink/40 text-ink flex items-center justify-center hover:bg-ink hover:text-canvas transition-colors z-[101]"
      >
        <X className="w-4 h-4" />
      </button>

      {showFilmArrows && (
        <>
          <button
            type="button"
            aria-label="Previous film"
            data-testid="video-modal-prev"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full border border-ink/40 text-ink hover:bg-ink hover:text-canvas flex items-center justify-center transition-colors z-[101]"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            aria-label="Next film"
            data-testid="video-modal-next"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full border border-ink/40 text-ink hover:bg-ink hover:text-canvas flex items-center justify-center transition-colors z-[101]"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        </>
      )}

      <div
        className="relative w-full max-w-[1300px] flex flex-col md:flex-row items-center gap-6 md:gap-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left meta rail — Title + Type */}
        {(title || typeLine) && (
          <div
            className="md:w-44 shrink-0 md:text-right text-ink"
            data-testid="video-modal-meta-left"
          >
            {title && (
              <h3 className="font-heading font-medium text-xl md:text-2xl leading-[1.1] tracking-[-0.01em] uppercase">
                {title}
              </h3>
            )}
            {typeLine && (
              <p className="mt-2 text-xs md:text-sm text-ink/70 tracking-[0.14em] uppercase">
                {typeLine}
              </p>
            )}
          </div>
        )}

        {/* Video + description column */}
        <div className="relative flex-1 flex flex-col gap-5 min-w-0">
          <div
            key={videoId}
            className="relative aspect-video bg-black shadow-2xl rounded-md overflow-hidden animate-fade-in"
          >
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
              title={title || "SAMURAI video"}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {description && (
            <p
              className="text-ink/80 text-sm md:text-[15px] leading-[1.65] font-light tracking-[-0.005em] max-h-[28vh] overflow-y-auto pr-1"
              data-testid="video-modal-description"
              style={{
                fontFamily:
                  "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif",
              }}
            >
              {description}
            </p>
          )}
        </div>

        {/* Right meta rail — Director + Agency */}
        {(director || agency) && (
          <div
            className="md:w-44 shrink-0 md:text-left text-ink"
            data-testid="video-modal-meta-right"
          >
            {director && (
              <p className="text-xs md:text-sm leading-relaxed">
                <span className="text-ink/55">{directorLabel}: </span>
                <span className="font-medium">{director}</span>
              </p>
            )}
            {agency && (
              <p className="mt-2 text-xs md:text-sm leading-relaxed">
                <span className="text-ink/55">Agency: </span>
                <span className="font-medium">{agency}</span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoModal;
